import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { EThemeType } from '../enums/theme-type.enum';
import { ClientId } from '@app-root-enums';
import { SiThemeService, ThemeType } from '@siemens/element-ng/theme';

export interface ThemeSettings {
    cssClass: string;
    logo: string;
    title: string;
    backgroundImage: string;

    links?: { [key: string]: string };
}

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    public siemensTheme: ThemeSettings = {
        cssClass: EThemeType.SIEMENS,
        logo: '/assets/svgs/logo-siemens.svg',
        title: 'main-scf-title',
        backgroundImage: '/assets/images/scf_design_visual.jpg'
    };

    public healthineersTheme: ThemeSettings = {
        cssClass: EThemeType.HEALTHINEERS,
        logo: '/assets/svgs/logo-healthineers.svg',
        title: 'main-scf-title-healtineers',
        backgroundImage: '/assets/images/scf_design_visual.jpg',

        links: {
            paperGuidelines: 'https://siemens-healthineers.bynder.com/share/0D4B20FE-6532-4967-9036E477AA6A9623/',
            yammer: 'https://www.yammer.com/siemenscrosscollaboration/#/threads/inGroup?type=in_group&feedId=7026008064&view=all'
        }
    };

    public energyTheme: ThemeSettings = {
        cssClass: EThemeType.ENERGY,
        logo: '/assets/svgs/SE_Logo_Color_RGB.svg',
        title: 'main-scf-title-energy',
        backgroundImage: '/assets/images/SE_Dot-Sphere_RGB_Type_3_Gradient.png'
    };

    public activeTheme = this.siemensTheme;
    public activeTheme$ = new BehaviorSubject<ThemeSettings>(this.siemensTheme);
    public energyThemeSet$ = new Subject<void>();

    private static readonly DARK_MODE_STORAGE_KEY = 'scf.darkMode';

    private readonly siThemeService = inject(SiThemeService);
    private readonly _isDarkMode = signal(false);
    public readonly isDarkMode = this._isDarkMode.asReadonly();

    constructor() {
        this.restoreDarkModeFromStorage();
    }
    get logo() {
        return this.activeTheme.logo;
    }

    get title() {
        return this.activeTheme.title;
    }

    get backgroundImage() {
        return this.activeTheme.backgroundImage;
    }

    private doc: Document = inject(DOCUMENT);

    /**
     * Sets the theme based on clientId or, if not provided, by inspecting the user's email domain.
     *
     * - If clientId is set, uses the corresponding theme.
     * - If clientId is not set, checks the mail:
     *   - '@siemens.com' → Siemens theme (default)
     *   - 'siemensgamesa.com' → Energy theme
     *   - 'siemens-healthineers' → Healthineers theme
     *   - Otherwise, Siemens theme (default)
     *
     * @param clientId The client ID string (optional)
     * @param mail The user's email address
     */
    public setThemeByClientId(clientId: string | undefined, mail: string) {
        if (clientId) {
            switch (Number(clientId)) {
                case ClientId.SIEMENS:
                    this.setTheme(this.siemensTheme);
                    return;
                case ClientId.HEALTHCARE:
                case ClientId.HEALTHCARE_CH:
                    this.setTheme(this.healthineersTheme);
                    return;
                case ClientId.FLENDER:
                case ClientId.ENERGY:
                    this.setTheme(this.energyTheme);
                    return;
                default:
                    this.setTheme(this.siemensTheme);
                    return;
            }
        }
        // Fallback: check mail domain
        if (mail) {
            const lowerMail = mail.toLowerCase();
            if (lowerMail.endsWith('@siemens.com')) {
                this.setTheme(this.siemensTheme);
                return;
            }
            if (lowerMail.includes('siemensgamesa.com')) {
                this.setTheme(this.energyTheme);
                return;
            }
            if (lowerMail.includes('siemens-healthineers')) {
                this.setTheme(this.healthineersTheme);
                return;
            }
        }
        // Default fallback
        this.setTheme(this.siemensTheme);
    }
    public setTheme(settings: ThemeSettings) {
        this.activeTheme = settings;
        this.doc.body.className = settings.cssClass;
        // Emit when Energy theme is set
        if (settings.cssClass === EThemeType.ENERGY) {
            this.energyThemeSet$.next();
        }
        this.activeTheme$.next(settings);
    }

    public toggleDarkMode(): void {
        const newMode: ThemeType = this._isDarkMode() ? 'light' : 'dark';
        this.siThemeService.applyThemeType(newMode);
        this._isDarkMode.set(newMode === 'dark');
        this.persistDarkMode(newMode === 'dark');
    }

    public getDarkModeIcon(): string {
        return this._isDarkMode() ? 'element-sun' : 'element-economy';
    }

    private restoreDarkModeFromStorage(): void {
        try {
            const stored = localStorage.getItem(ThemeService.DARK_MODE_STORAGE_KEY);
            if (stored === null) {
                return;
            }
            const isDark = stored === 'true';
            const mode: ThemeType = isDark ? 'dark' : 'light';
            this.siThemeService.applyThemeType(mode);
            this._isDarkMode.set(isDark);
        } catch {
            // localStorage may be unavailable (SSR, privacy mode) — ignore
        }
    }

    private persistDarkMode(isDark: boolean): void {
        try {
            localStorage.setItem(ThemeService.DARK_MODE_STORAGE_KEY, String(isDark));
        } catch {
            // ignore
        }
    }
}
